import {type FC, useCallback, useEffect, useRef, useState} from 'react';
import {
    BackgroundImg,
    FullScreenIcon,
    LayoutButton,
    LayoutItem,
    PleaseWaitText,
    SplashImg,
    StreamingWrapper,
} from './types/Streaming.styled.ts';
import TimelineBar from './component/TimelineBar.tsx';
import useCenterStore from '../../store/useCenterStore.ts';
import useStreamingStore from '../../store/useStreamingStore.ts';
import Streaming from './component/Streaming.tsx';
import useGlobalStore from '../../store/useGlobalStore.ts';
import {setFocus, useFocusable} from '@noriginmedia/norigin-spatial-navigation';
import VodPlayer from './component/VodPlayer.tsx';
import NoStreaming from './component/NoStreaming.tsx';
import FullScreenSVG from '../../assets/broadcast/scale-expanding.svg';
import n1 from '../../assets/layout-icon/new-1.png';
import n2 from '../../assets/layout-icon/new-2.png';
import n3 from '../../assets/layout-icon/new-3.png';
import n4 from '../../assets/layout-icon/new-4.svg';
import n6 from '../../assets/layout-icon/new-5.svg';
import n5 from '../../assets/layout-icon/new-6.svg';
import n1Active from '../../assets/layout-icon/new-1-active.png';
import n2Active from '../../assets/layout-icon/new-2-active.png';
import n3Active from '../../assets/layout-icon/new-3-active.png';
import n4Active from '../../assets/layout-icon/new-4-active.svg';
import n6Active from '../../assets/layout-icon/new-5-active.svg';
import n5Active from '../../assets/layout-icon/new-6-active.svg';

import {isBroadcastOver} from '../../utils/StreamingUtil.ts';
import callApi from '../../apis/callApi.ts';
import type {TStudioMode} from './types';

const StreamingPage: FC = () => {
    const {videoMode, centerInfo, allDevices, setAllDevices} = useCenterStore();
    const {
        state,
        setIsSplash,
        scheduledData,
        setScheduledData,
        streamingPollingData,
        setJitsiToken,
        setGridLayout,
        setStreamingState,
        isSplashOn,
        splashMessage,
        setSplashMessage,
    } = useStreamingStore();
    const tvScheduleInterval = useRef<ReturnType<typeof setTimeout> | null>(null);

    useEffect(() => {
        setFocus('streaming');
        setIsSplash(true);
        setSplashMessage('방송 편성표를 불러오는 중...');
    }, []);

    const fetchData = async () => {
        const isSplash = useStreamingStore.getState().isSplashOn;
        if (isSplash) setSplashMessage('방송 편성표를 불러오는 중...');
        const {data} = await callApi().get('/tvs/schedule/client/');
        setScheduledData(data.slides[0]?.events ?? []);
        if (allDevices.length < 3) {
            if (isSplash) setSplashMessage('기기 정보를 불러오는 중...');
            await setAllDevices();
        }
        if (centerInfo && centerInfo.center_id) {
            if (isSplash) setSplashMessage('방송 인증 정보를 확인하는 중...');
            const token = await setJitsiToken(centerInfo?.center_id);
            setStreamingState({jwt: token});
        }
        if (isSplash) setSplashMessage('방송 정보를 확인하는 중...');
    };

    useEffect(() => {
        if (centerInfo?.id) {
            fetchData();
            tvScheduleInterval.current = setInterval(fetchData, 60000);
            return () => {
                if (tvScheduleInterval.current) clearInterval(tvScheduleInterval.current);
                tvScheduleInterval.current = null;
            };
        }
    }, [centerInfo]);

    // streamingPollingData 도착 시: live가 아니면 스플래시 해제
    useEffect(() => {
        if (!isSplashOn || !streamingPollingData) return;
        if (streamingPollingData.broadcast.content_type === 'live') {
            setSplashMessage('방송 서버에 연결하는 중...');
        } else {
            setSplashMessage('준비가 완료되었습니다!');
            const timer = setTimeout(() => setIsSplash(false), 1000);
            return () => clearTimeout(timer);
        }
    }, [streamingPollingData, isSplashOn]);

    // 안전 타임아웃: 20초 후에도 스플래시가 남아있으면 강제 해제
    useEffect(() => {
        if (!isSplashOn) return;
        const timer = setTimeout(() => setIsSplash(false), 20000);
        return () => clearTimeout(timer);
    }, [isSplashOn]);

    const {isFullScreen, setFullScreen} = useGlobalStore();
    const [showScreenIcons, setShowScreenIcons] = useState(true);
    const hideTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const showIconAndStartTimer = useCallback(() => {
        setShowScreenIcons(true);
        if (hideTimeoutRef.current) {
            clearTimeout(hideTimeoutRef.current);
        }
        hideTimeoutRef.current = setTimeout(() => {
            setShowScreenIcons(false);
        }, 3000);
    }, []);

    useEffect(() => {
        const handleFocusChange = () => {
            showIconAndStartTimer();
        };
        document.addEventListener('keydown', handleFocusChange);
        document.addEventListener('focusin', handleFocusChange);
        showIconAndStartTimer();
        return () => {
            document.removeEventListener('keydown', handleFocusChange);
            document.removeEventListener('focusin', handleFocusChange);
            if (hideTimeoutRef.current) {
                clearTimeout(hideTimeoutRef.current);
            }
        };
    }, [showIconAndStartTimer]);

    const [selectedLayout, setSelectedLayout] = useState<string>("new-32");

    const prevStudioLayout = useRef<string>('');

    // 스튜디오가 POST한 studio_state.layout을 현재 그리드 레이아웃에 반영.
    // 변경 시에만 발화(prevStudioLayout ref) → 매 폴링마다 사용자 수동 선택을 덮어쓰지 않음.
    // 미지 문자열은 무시(가드).
    useEffect(() => {
        const studioLayout = streamingPollingData?.studio_state?.layout ?? '';
        if (!studioLayout || studioLayout === prevStudioLayout.current) return;
        prevStudioLayout.current = studioLayout;

        const KNOWN_MODES: readonly TStudioMode[] = [
            'pip', 'new-4', 'new-8', 'new-28', 'new-32', 'new-40', 'new-52',
            '44', '6b', '6r', 'best-perform', 'direct-chat',
        ] as const;

        if (KNOWN_MODES.includes(studioLayout as TStudioMode)) {
            setSelectedLayout(studioLayout);
            setGridLayout({mode: studioLayout as TStudioMode, sub: true});
        }
    }, [streamingPollingData?.studio_state?.layout]);

    const {ref: firstLayoutRef, focused: firstLayoutFocused} = useFocusable({
        focusKey: 'pip',
        onEnterPress: () => {
            setSelectedLayout('pip');
            setGridLayout({mode: 'pip', sub: true});
        },
        onArrowPress: (direction) => {
            return direction !== 'left';
        },
    });
    const {ref: secondLayoutRef, focused: secondLayoutFocused} = useFocusable({
        focusKey: 'new-8',
        onEnterPress: () => {
            setSelectedLayout('new-8');
            setGridLayout({mode: 'new-8', sub: true});
        },
    });
    const {ref: fourthLayoutRef, focused: fourthLayoutFocused} = useFocusable({
        focusKey: 'new-32',
        onEnterPress: () => {
            setSelectedLayout('new-32');
            setGridLayout({mode: 'new-32', sub: true});
        },
    });
    const {ref: fifthLayoutRef, focused: fifthLayoutFocused} = useFocusable({
        focusKey: 'new-52',
        onEnterPress: () => {
            setSelectedLayout('new-52');
            setGridLayout({mode: 'new-52', sub: true});
        },
    });
    const {ref: autoLayoutRef, focused: autoLayoutFocused} = useFocusable({
        focusKey: '44',
        onEnterPress: () => {
            setSelectedLayout('44');
            setGridLayout({mode: '44', sub: true});
        },
    });

    const setFullMode = useCallback(() => {
        setFullScreen(!isFullScreen);
        if (!isFullScreen) {
            setGridLayout({mode: 'pip', sub: true});
        } else {
            setGridLayout({mode: selectedLayout as TStudioMode, sub: true});
        }
    }, [isFullScreen, selectedLayout]);

    const {ref: fullIconRef, focused: fullIconFocused} = useFocusable({
        focusKey: 'streaming-page-full-icon',
        onEnterPress: () => setFullMode(),
        onFocus: () => {
            showIconAndStartTimer();
        },
        onArrowPress: (direction) => {
            if (isFullScreen) {
                return false;
            }
            if (direction === 'up') {
                return true;
            }
            if (streamingPollingData && streamingPollingData.broadcast.content_type === 'live') {
                return direction === 'left';
            } else {
                if (direction === 'left') setFocus('streaming');
                return false;
            }
        },
    });

    return (
        <StreamingWrapper>
            {
                <SplashImg $show={isSplashOn}>
                    <PleaseWaitText>
                        <div>{splashMessage}</div>
                    </PleaseWaitText>
                </SplashImg>
            }

            {/* 방송이 있을 경우 (스트리밍) */}
            {streamingPollingData && streamingPollingData.broadcast.content_type === 'live' && state.jwt &&
                <Streaming/>}

            {/* 방송이 있을 경우 (VOD) */}
            {streamingPollingData && streamingPollingData.broadcast.content_type === 'vod' && (
                // <Streaming/>
                <VodPlayer
                    videoUrl={streamingPollingData.broadcast.vod!.video}
                    startTime={streamingPollingData.broadcast.start_time}
                    endTime={streamingPollingData.broadcast.end_time}
                />
            )}

            {/* 방송이 없을 경우 */}
            {streamingPollingData && streamingPollingData.broadcast.content_type === null && (
                // <Streaming/>
                <NoStreaming/>
            )}

            {/* 방송이 있을 경우 (타임 라인) */}
            {streamingPollingData && scheduledData && !isBroadcastOver(scheduledData) && (
                <TimelineBar events={scheduledData}/>
            )}

            {streamingPollingData && streamingPollingData.broadcast.content_type === 'live' && (
                <LayoutButton $show={showScreenIcons} $isFullScreen={isFullScreen}>
                    {/*2 개*/}
                    <LayoutItem
                        ref={firstLayoutRef}
                        $focused={firstLayoutFocused}
                        $disabled={false}
                    >
                        <img src={selectedLayout === 'pip' ? n1Active : n1} alt="first-layout"/>
                    </LayoutItem>
                    {/*8 개*/}
                    <LayoutItem
                        ref={secondLayoutRef}
                        $focused={secondLayoutFocused}
                    >
                        <img src={selectedLayout === 'new-8' ? n2Active : n2} alt="second-layout"/>
                    </LayoutItem>
                    {/*68 개*/}
                    <LayoutItem
                        ref={fifthLayoutRef}
                        $focused={fifthLayoutFocused}
                    >
                        <img src={selectedLayout === 'new-52' ? n4Active : n4} alt="fifth-layout"/>
                    </LayoutItem>
                    {/*38 개*/}
                    <LayoutItem
                        ref={autoLayoutRef}
                        $focused={autoLayoutFocused}
                    >
                        <img src={selectedLayout === '44' ? n6Active : n6} alt="auto-layout"/>
                    </LayoutItem>
                    {/*26 개*/}
                    <LayoutItem
                        ref={fourthLayoutRef}
                        $focused={fourthLayoutFocused}
                    >
                        <img src={selectedLayout === 'new-32' ? n5Active : n5} alt="fourth-layout"/>
                    </LayoutItem>
                </LayoutButton>
            )}

            {isFullScreen && <BackgroundImg/>}

            <FullScreenIcon
                ref={fullIconRef}
                $show={showScreenIcons}
                $focused={fullIconFocused}
                onClick={() => setFullScreen(!isFullScreen)}
                $isFullScreen={isFullScreen}
            >
                <img src={FullScreenSVG} alt="full-screen-icon"/>
            </FullScreenIcon>
        </StreamingWrapper>
    );
};

export default StreamingPage;
